<template>
	<div>
		<div class="content" id="pagination-object">
			<p>
				You che react to pagination change by synching an object on the <code>pagination</code> property.<br>
				This, combined with the <code>hidePagination</code> property, allows you to manage the pagination externally:
			</p>
			<pre v-highlightjs class="box pa-0"><code class="html" v-pre>&lt;template&gt;
	&lt;div&gt;
		&lt;div class=&quot;pagination&quot;&gt;
			&lt;button @click=&quot;previousPage&quot; :disabled=&quot;prevDisabled&quot;&gt;previous&lt;/button&gt;
			&lt;select v-model=&quot;pagination.page&quot;&gt;
				&lt;option v-for=&quot;page in pages&quot; :value=&quot;page&quot; :key=&quot;page&quot;&gt;page {{page}}&lt;/option&gt;
			&lt;/select&gt;
			&lt;select v-model.number=&quot;pagination.max&quot;&gt;
				&lt;option :value=&quot;1&quot;&gt;max 1&lt;/option&gt;
				&lt;option :value=&quot;2&quot;&gt;max 2&lt;/option&gt;
				&lt;option :value=&quot;5&quot;&gt;max 5&lt;/option&gt;
				&lt;option :value=&quot;10&quot;&gt;max 10&lt;/option&gt;
			&lt;/select&gt;
			&lt;button @click=&quot;nextPage&quot; :disabled=&quot;nextDisabled&quot;&gt;next&lt;/button&gt;
		&lt;/div&gt;
		&lt;ui-table :items=&quot;items&quot; multisort striped :pagination.sync=&quot;pagination&quot; fixed-layout hide-pagination&gt;
			&lt;ui-table-column prop=&quot;group&quot; sortable/&gt;
			&lt;ui-table-column prop=&quot;user&quot;/&gt;
			&lt;ui-table-column prop=&quot;age&quot; sortable/&gt;
		&lt;/ui-table&gt;
	&lt;/div&gt;
&lt;/template&gt;

&lt;script&gt;
	import { times, head, last } from &quot;lodash&quot;

	export default {
		data() {
			return {
				pagination: {
					sort: {},
					max: 5,
					page: 2
				},
				items: [
					{ group: "foo", user: "User 1", age: 44 },
					{ group: "bar", user: "User 2", age: 21 },
					{ group: "foo", user: "User 3", age: 33 },
					{ group: "foo", user: "User 4", age: 37 },
					{ group: "bar", user: "User 5", age: 55 },
					{ group: "foo", user: "User 6", age: 18 },
					{ group: "bar", user: "User 7", age: 25 },
					{ group: "bar", user: "User 8", age: 61 },
					{ group: "bar", user: "User 9", age: 44 },
					{ group: "bar", user: "User 10", age: 21 }
				]
			}
		},
		computed: {
			pages() { return times(Math.ceil(this.items.length / Math.abs(this.pagination.max)), n =&gt; n + 1) },
			prevDisabled() { return this.pagination.page === head(this.pages) },
			nextDisabled() { return this.pagination.page === last(this.pages) }
		},
		methods: {
			previousPage() { if(!this.prevDisabled) this.pagination.page -= 1 },
			nextPage() { this.pagination.page += 1 }
		}
	}
&lt;/script&gt;</code></pre>
		</div>
		<div class="box">
			<div class="field has-addons">
				<p class="control">
					<button class="button" @click="previousPage" :disabled="prevDisabled">
						<span class="icon is-small">
							<i class="fas fa-arrow-left"></i>
						</span>
						<span>previous</span>
					</button>
				</p>
				<p class="control is-hidden-touch">
					<span class="select">
						<select v-model="pagination.page">
							<option v-for="page in pages" :value="page" :key="page">page {{page}}</option>
						</select>
					</span>
				</p>
				<p class="control is-hidden-touch">
					<span class="select">
						<select v-model.number="pagination.max">
							<option :value="1">max 1</option>
							<option :value="2">max 2</option>
							<option :value="5">max 5</option>
							<option :value="10">max 10</option>
						</select>
					</span>
				</p>
				<p class="control">
					<button class="button" @click="nextPage" :disabled="nextDisabled">
						<span>next</span>
						<span class="icon is-small">
							<i class="fas fa-arrow-right"></i>
						</span>
					</button>
				</p>
			</div>
			<ui-table :items="items" multisort striped :pagination.sync="pagination" fixed-layout hide-pagination>
				<ui-table-column prop="group" sortable/>
				<ui-table-column prop="user"/>
				<ui-table-column prop="age" sortable/>
			</ui-table>
		</div>
	</div>
</template>

<script>
    import times from "lodash/times"
    import head from "lodash/head"
    import last from "lodash/last"

    export default {
        name: "pagination-object",
		data() {
            return {
                pagination: {
                    sort: {},
                    max: 5,
					page: 2
				},
                items: [
                    { group: "foo", user: "User 1", age: 44 },
                    { group: "bar", user: "User 2", age: 21 },
                    { group: "foo", user: "User 3", age: 33 },
                    { group: "foo", user: "User 4", age: 37 },
                    { group: "bar", user: "User 5", age: 55 },
                    { group: "foo", user: "User 6", age: 18 },
                    { group: "bar", user: "User 7", age: 25 },
                    { group: "bar", user: "User 8", age: 61 },
                    { group: "bar", user: "User 9", age: 44 },
                    { group: "bar", user: "User 10", age: 21 }
                ]
			}
		},
		computed: {
            pages() { return times(Math.ceil(this.items.length / Math.abs(this.pagination.max)), n => n + 1) },
			prevDisabled() { return this.pagination.page === head(this.pages) },
			nextDisabled() { return this.pagination.page === last(this.pages) }
		},
		methods: {
			previousPage() { if(!this.prevDisabled) this.pagination.page -= 1 },
            nextPage() { this.pagination.page += 1 }
		}
    }
</script>

<style lang="scss" scoped>
	.field.has-addons { justify-content: center; }
</style>